import { useSyncExternalStore } from 'react';

export const EXPIRATION_PREFERENCES_KEY = 'trackeat:expiration-preferences:v1:';
export const EXPIRATION_PREFERENCES_EVENT = 'trackeat:expiration-preferences-changed';

export const EXPIRATION_ALERT_LEAD_OPTIONS = [24, 48, 72, 168, 336] as const;
export const EXPIRATION_REMINDER_LEAD_OPTIONS = [168, 72, 48, 24, 12, 6, 3, 1] as const;

export interface ExpirationPreferences {
  alertLeadHours: number;
  reminderLeadHours: number[];
}

export const DEFAULT_EXPIRATION_PREFERENCES: ExpirationPreferences = {
  alertLeadHours: 48,
  reminderLeadHours: [24, 3],
};

const cachedPreferences = new Map<string, { raw: string | null; value: ExpirationPreferences }>();

function isAllowed(value: number, options: readonly number[]): boolean {
  return Number.isFinite(value) && options.includes(value);
}

export function normalizeExpirationPreferences(value: unknown): ExpirationPreferences {
  if (!value || typeof value !== 'object') return DEFAULT_EXPIRATION_PREFERENCES;
  const input = value as Partial<ExpirationPreferences>;
  const alertLeadHours = Number(input.alertLeadHours);
  const reminderLeadHours = Array.isArray(input.reminderLeadHours)
    ? [...new Set(input.reminderLeadHours.map(Number))]
      .filter((hours) => isAllowed(hours, EXPIRATION_REMINDER_LEAD_OPTIONS))
      .sort((a, b) => b - a)
      .slice(0, 2)
    : DEFAULT_EXPIRATION_PREFERENCES.reminderLeadHours;

  return {
    alertLeadHours: isAllowed(alertLeadHours, EXPIRATION_ALERT_LEAD_OPTIONS)
      ? alertLeadHours
      : DEFAULT_EXPIRATION_PREFERENCES.alertLeadHours,
    reminderLeadHours,
  };
}

export function formatExpirationLead(hours: number): string {
  if (hours % 24 === 0) {
    const days = hours / 24;
    return days === 1 ? '1 giorno' : `${days} giorni`;
  }
  return hours === 1 ? '1 ora' : `${hours} ore`;
}

export function isWithinExpirationLead(
  expirationDate: string | null | undefined,
  leadHours: number,
  now = Date.now(),
): boolean {
  if (!expirationDate) return false;
  const expiresAt = Date.parse(expirationDate);
  return Number.isFinite(expiresAt) && expiresAt - now < leadHours * 3_600_000;
}

function storageKey(scopeId: string | null): string {
  return EXPIRATION_PREFERENCES_KEY + (scopeId ?? 'device');
}

export function readExpirationPreferences(scopeId: string | null = null): ExpirationPreferences {
  if (typeof localStorage === 'undefined') return DEFAULT_EXPIRATION_PREFERENCES;
  try {
    const key = storageKey(scopeId);
    const raw = localStorage.getItem(key);
    const cached = cachedPreferences.get(key);
    if (cached?.raw === raw) return cached.value;
    const value = raw
      ? normalizeExpirationPreferences(JSON.parse(raw))
      : DEFAULT_EXPIRATION_PREFERENCES;
    cachedPreferences.set(key, { raw, value });
    return value;
  } catch {
    return DEFAULT_EXPIRATION_PREFERENCES;
  }
}

export function writeExpirationPreferences(
  value: ExpirationPreferences,
  scopeId: string | null = null,
): ExpirationPreferences {
  const normalized = normalizeExpirationPreferences(value);
  try {
    const key = storageKey(scopeId);
    const raw = JSON.stringify(normalized);
    cachedPreferences.set(key, { raw, value: normalized });
    localStorage.setItem(key, raw);
    window.dispatchEvent(new Event(EXPIRATION_PREFERENCES_EVENT));
  } catch {
    // Storage non disponibile: la sessione corrente continua con i default.
  }
  return normalized;
}

function subscribe(scopeId: string | null, listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const key = storageKey(scopeId);
  const onStorage = (event: StorageEvent) => {
    if (event.key === key) listener();
  };
  window.addEventListener('storage', onStorage);
  window.addEventListener(EXPIRATION_PREFERENCES_EVENT, listener);
  return () => {
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(EXPIRATION_PREFERENCES_EVENT, listener);
  };
}

export function useExpirationPreferences(scopeId: string | null): ExpirationPreferences {
  return useSyncExternalStore(
    (listener) => subscribe(scopeId, listener),
    () => readExpirationPreferences(scopeId),
    () => DEFAULT_EXPIRATION_PREFERENCES,
  );
}
